import React from 'react';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Pagination from 'react-js-pagination';
import {injectIntl} from 'react-intl';

import Loader from '../dashboard/Loader';
import ErrorModal from '../ui/ErrorModal';
import API from '../../common/utils/API';
import {DateTimeCell, VenueLocalDateTimeCell} from './formatDateTime';
import {BandBadge} from './BandBadge';

const TABLE_SCROLL_HEIGHT = 320;
const COMPUTED_PAGE_SIZE = 50;

export class PromotedTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            manual: [],
            computed: [],
            total: 0,
            limit: 50,
            applyEnabled: false,
            featuredCategoryId: null,
            computedPage: 1,
            eventIdInput: '',
            eventIdFilter: null,
        };

        this.fetchData = this.fetchData.bind(this);
        this.handleComputedPageChange = this.handleComputedPageChange.bind(
            this,
        );
        this.handleFilterSubmit = this.handleFilterSubmit.bind(this);
        this.handleFilterClear = this.handleFilterClear.bind(this);
    }

    async componentDidMount() {
        await this.fetchData();
    }

    async fetchData() {
        this.setState({loading: true});

        try {
            const response = await API.reports.getRelevancePromoted({
                limit: COMPUTED_PAGE_SIZE,
                page: this.state.computedPage,
                ...(this.state.eventIdFilter
                    ? {eventId: this.state.eventIdFilter}
                    : {}),
            });

            this.setState({
                manual: response.manual,
                computed: response.computed,
                total: response.total,
                limit: response.limit,
                applyEnabled: response.applyEnabled,
                featuredCategoryId: response.featuredCategoryId,
                loading: false,
                error: false,
            });
        } catch (e) {
            this.setState({loading: false, error: true});
        }
    }

    async handleComputedPageChange(page) {
        await new Promise((resolve) =>
            this.setState({computedPage: page}, resolve),
        );
        await this.fetchData();
    }

    async handleFilterSubmit(e) {
        e.preventDefault();
        const id = parseInt(this.state.eventIdInput, 10);
        await new Promise((resolve) =>
            this.setState(
                {eventIdFilter: id > 0 ? id : null, computedPage: 1},
                resolve,
            ),
        );
        await this.fetchData();
    }

    async handleFilterClear() {
        await new Promise((resolve) =>
            this.setState(
                {eventIdInput: '', eventIdFilter: null, computedPage: 1},
                resolve,
            ),
        );
        await this.fetchData();
    }

    render() {
        const intl = this.props.intl;
        const {
            manual,
            computed,
            total,
            limit,
            applyEnabled,
            featuredCategoryId,
            loading,
            error,
            computedPage,
            eventIdInput,
            eventIdFilter,
        } = this.state;
        const effectiveApplyEnabled =
            applyEnabled && featuredCategoryId !== null;
        const isEmpty = manual.length === 0 && computed.length === 0;

        return (
            <div
                data-test='promotedTableComponent'
                style={{position: 'relative'}}
            >
                <Loader loading={loading} />
                <div className='d-flex justify-content-between mb-2'>
                    <Form
                        inline
                        onSubmit={this.handleFilterSubmit}
                        data-test='promotedEventIdFilter'
                    >
                        <Form.Control
                            size='sm'
                            type='number'
                            min='1'
                            style={{width: 140}}
                            value={eventIdInput}
                            placeholder={intl.formatMessage({
                                id: 'RELEVANCE.PROMOTED_FILTER_EVENT_ID',
                            })}
                            onChange={(e) =>
                                this.setState({eventIdInput: e.target.value})
                            }
                            data-test='promotedEventIdInput'
                        />
                        <Button
                            type='submit'
                            variant='primary'
                            size='sm'
                            className='ml-2'
                            data-test='promotedEventIdApply'
                        >
                            {intl.formatMessage({
                                id: 'RELEVANCE.PROMOTED_FILTER_APPLY',
                            })}
                        </Button>
                        {(eventIdFilter || eventIdInput) && (
                            <Button
                                variant='link'
                                size='sm'
                                onClick={this.handleFilterClear}
                                data-test='promotedEventIdClear'
                            >
                                {intl.formatMessage({
                                    id: 'RELEVANCE.PROMOTED_FILTER_CLEAR',
                                })}
                            </Button>
                        )}
                    </Form>
                    <Button
                        variant='secondary'
                        size='sm'
                        onClick={this.fetchData}
                        data-test='promotedRefreshButton'
                    >
                        {intl.formatMessage({id: 'RELEVANCE.REFRESH_BUTTON'})}
                    </Button>
                </div>
                {!loading && !effectiveApplyEnabled && (
                    <Alert
                        variant='warning'
                        data-test='promotedApplyDisabledWarning'
                    >
                        {intl.formatMessage({
                            id: 'RELEVANCE.APPLY_DISABLED_WARNING',
                        })}
                    </Alert>
                )}
                {isEmpty ? (
                    <p data-test='promotedEmptyState'>
                        {intl.formatMessage({id: 'RELEVANCE.PROMOTED_EMPTY'})}
                    </p>
                ) : (
                    <>
                        <Card className='mb-4'>
                            <Card.Header>
                                {intl.formatMessage({
                                    id: 'RELEVANCE.PROMOTED_MANUAL_HEADER',
                                })}
                            </Card.Header>
                            <Card.Body>
                                <div
                                    data-test='promotedManualTableScroll'
                                    style={{
                                        maxHeight: TABLE_SCROLL_HEIGHT,
                                        overflowY: 'auto',
                                    }}
                                >
                                    <Table
                                        striped
                                        bordered
                                        hover
                                        size='sm'
                                        data-test='promotedManualTable'
                                    >
                                        <thead>
                                            <tr>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_ID',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_NAME',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_STARTS_AT',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_PRIORITY',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_PINNED_UNTIL',
                                                    })}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {manual.map((event) => (
                                                <tr
                                                    key={event.eventId}
                                                    data-test='promotedManualRow'
                                                    onClick={() =>
                                                        this.props.onSelectEvent(
                                                            event.eventId,
                                                        )
                                                    }
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <td>{event.eventId}</td>
                                                    <td>{event.name}</td>
                                                    <td>
                                                        <VenueLocalDateTimeCell
                                                            iso={event.startsAt}
                                                        />
                                                    </td>
                                                    <td>
                                                        {event.priorityOrder}
                                                    </td>
                                                    <td>
                                                        <DateTimeCell
                                                            iso={
                                                                event.pinnedUntil
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className='mb-4'>
                            <Card.Header>
                                {intl.formatMessage(
                                    {
                                        id:
                                            'RELEVANCE.PROMOTED_COMPUTED_HEADER',
                                    },
                                    {shown: computed.length, total, limit},
                                )}
                            </Card.Header>
                            <Card.Body>
                                <div
                                    data-test='promotedComputedTableScroll'
                                    style={{
                                        maxHeight: TABLE_SCROLL_HEIGHT,
                                        overflowY: 'auto',
                                    }}
                                >
                                    <Table
                                        striped
                                        bordered
                                        hover
                                        size='sm'
                                        data-test='promotedComputedTable'
                                    >
                                        <thead>
                                            <tr>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_ID',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_NAME',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_STARTS_AT',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_BAND',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_SCORE',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_N',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_Z',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_AUTO_RANKING',
                                                    })}
                                                </th>
                                                <th>
                                                    {intl.formatMessage({
                                                        id:
                                                            'RELEVANCE.PROMOTED_TABLE_DEMAND_BANNER',
                                                    })}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {computed.map((event) => (
                                                <tr
                                                    key={event.eventId}
                                                    data-test='promotedComputedRow'
                                                    onClick={() =>
                                                        this.props.onSelectEvent(
                                                            event.eventId,
                                                        )
                                                    }
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <td>{event.eventId}</td>
                                                    <td>{event.name}</td>
                                                    <td>
                                                        <VenueLocalDateTimeCell
                                                            iso={event.startsAt}
                                                        />
                                                    </td>
                                                    <td>
                                                        <BandBadge
                                                            intl={intl}
                                                            band={event.band}
                                                        />
                                                    </td>
                                                    <td>{event.score}</td>
                                                    <td>{event.n}</td>
                                                    <td>{event.z}</td>
                                                    <td data-test='promotedComputedAutoRanking'>
                                                        {intl.formatMessage({
                                                            id: event.autoRankingEnabled
                                                                ? 'RELEVANCE.PROMOTED_AUTO_RANKING_ON'
                                                                : 'RELEVANCE.PROMOTED_AUTO_RANKING_OFF',
                                                        })}
                                                    </td>
                                                    <td data-test='promotedComputedDemandBanner'>
                                                        {intl.formatMessage({
                                                            id: event.demandBannerEnabled
                                                                ? 'RELEVANCE.PROMOTED_AUTO_RANKING_ON'
                                                                : 'RELEVANCE.PROMOTED_AUTO_RANKING_OFF',
                                                        })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {total > limit && (
                                    <div className='d-flex justify-content-end mt-2'>
                                        <Pagination
                                            itemClass='page-item'
                                            linkClass='page-link'
                                            activePage={computedPage}
                                            itemsCountPerPage={limit}
                                            totalItemsCount={total}
                                            pageRangeDisplayed={5}
                                            onChange={
                                                this.handleComputedPageChange
                                            }
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </>
                )}
                <ErrorModal
                    isLoadErrorModalOpen={error}
                    toggleLoadErrorModal={() => this.setState({error: false})}
                    errorMessage={intl.formatMessage({
                        id: 'RELEVANCE.LOAD_ERROR',
                    })}
                    handleRetryClick={this.fetchData}
                    returnUrl='/relevance'
                    loading={loading}
                />
            </div>
        );
    }
}

export default injectIntl(PromotedTable);
